<script>
import { mapActions, mapState } from 'vuex';
import mainViewTabMixin from '../../mixins/mainViewTab';
import paginationMixin from '../../mixins/pagination';
import { affiliateListHeaders } from './config';
import AffiliateListItem from './components/AffiliateListItem';
import NewAffiliateDialog from './components/NewAffiliateDialog';
import RbxConfirmDialog from '../../shared/reybex/RbxConfirmDialog';
import CsvImportDialog from './components/CsvImportDialog';

export default {
  name: 'AffiliateList',
  components: {
    AffiliateListItem,
    NewAffiliateDialog,
    RbxConfirmDialog,
    CsvImportDialog,
  },
  mixins: [mainViewTabMixin, paginationMixin],
  data() {
    return {
      isInitialized: false,
      headers: affiliateListHeaders,
      showNewAffiliateDialog: false,
      isConfirmDialogDisplayed: false,
      confirmDeletionMessage: '',
      affiliateToDelete: {},
      searchTerm: '',
      isDeleteFilter: false,
      isDeleteOptions: [{ id: null, text: 'alle' }, { id: true, text: 'ja' }, { id: false, text: 'nein' }],
      isCsvImportDialogDisplayed: false,
    };
  },
  computed: {
    ...mapState(['affiliateList']),
    ...mapState('affiliateList', ['data', 'meta', 'isLoading', 'hasError']),
    filteredItems() {
      const self = this;

      if (!this.data) return [];
      if (this.isDeleteFilter == null) return this.data;

      return this.data.filter(it => it.isDelete === self.isDeleteFilter);
    },
  },
  mounted() {
    this.fetchData(this.paginationParams);
    this.isInitialized = true;
  },
  methods: {
    ...mapActions('affiliateList', ['fetchAffiliateList']),
    ...mapActions('affiliateList', { deleteAffiliate: 'deleteAffiliate' }),
    fetchData(options) {
      return this.fetchAffiliateList(options);
    },
    onDelete(affiliateToDelete) {
      this.affiliateToDelete = affiliateToDelete;
      this.confirmDeletionMessage =
        `Soll der Vertragspartner ${this.affiliateToDelete.name} gelöscht werden?`;
      this.isConfirmDialogDisplayed = true;
    },
    onRefuse() {
      this.isConfirmDialogDisplayed = false;
    },
    onConfirm() {
      this.deleteAffiliate(this.affiliateToDelete)
        .then(() => {
          this.fetchData().then(() => {
            this.isInitialized = true;
          });
          this.isConfirmDialogDisplayed = false;
        });
    },
    onSearch() {
      this.paginationParams.searchTerm = this.searchTerm;
      this.fetchData(this.paginationParams);
    },
  },
};
</script>

<template>
  <div class="affiliate-list">
    <template v-if="affiliateList.hasError">
      <p class="js-error-template ma-3">Failed to fetch affiliate list. Please try again!</p>
    </template>
    <template v-else>
      <v-toolbar dark dense height="40" color="blue-grey lighten-2">
        <v-tooltip bottom>
          <v-btn icon slot="activator" @click="showNewAffiliateDialog = true">
            <v-icon>add</v-icon>
          </v-btn>
          <span>Hinzufügen</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn @click="fetchData" icon slot="activator">
            <v-icon>refresh</v-icon>
          </v-btn>
          <span>Aktualisieren</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn @click="isCsvImportDialogDisplayed = true" icon slot="activator">
            <v-icon>cloud_upload</v-icon>
          </v-btn>
          <span>CSV Import</span>
        </v-tooltip>

        <v-spacer />
        <v-flex md3 >
          <v-text-field
            v-model="searchTerm"
            label="Suche"
            height="22"
            class="caption"
            @keyup.enter="onSearch"
            @blur="onSearch"
          />
        </v-flex>
      </v-toolbar>

      <v-flex xs4 md2>
        <v-select
          style="padding-left: 22px; padding-top: 25px;"
          :items="isDeleteOptions"
          v-model="isDeleteFilter"
          item-value="id"
          item-text="text"
          label="Gelöscht" />
      </v-flex>

      <v-progress-linear
        v-if="affiliateList.isLoading && !isInitialized"
        :indeterminate="true"
        class="js-loading ma-0"
      />

      <v-data-table
        v-else
        :headers="headers"
        :items="filteredItems"
        :disable-initial-sort="true"
        :pagination="pagination"
        :loading="affiliateList.isLoading"
        :rows-per-page-items="rowsPerPageItems"
        :total-items="this.pagination.totalItems"
        :expand="true"
        item-key="id"
        @update:pagination="loadNextPage"
        rows-per-page-text="Zeilen pro Seite"
        no-data-text="keine Daten verfügbar"
        class="elevation-1 js-table"
      >
        <template slot="headers" slot-scope="props">
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              :class="['column sortable',
                       pagination.descending ? 'desc' : 'asc',
                       header.value === pagination.sortBy ? 'active' : '']"
              @click="changeSort(header.value, data)"
            >
              <v-icon small>arrow_upward</v-icon>
              {{ header.text }}
            </th>
          </tr>
        </template>

        <template slot="items" slot-scope="props">
          <affiliate-list-item
            :affiliate="props.item"
            :expanded="props.expanded"
            @delete="onDelete"
          />
        </template>
        <template slot="pageText" slot-scope="{ pageStart, pageStop }">
          von {{ pageStart }} bis {{ pageStop }}
        </template>
      </v-data-table>

      <NewAffiliateDialog
        v-if="showNewAffiliateDialog"
        @cancel="showNewAffiliateDialog = false"
      />

      <csv-import-dialog
        v-if="isCsvImportDialogDisplayed"
        @cancel="isCsvImportDialogDisplayed = false"
        @imported="fetchData"
      />

      <rbx-confirm-dialog @confirm="onConfirm" @refuse="onRefuse"
                          :message="confirmDeletionMessage"
                          :is-displayed="isConfirmDialogDisplayed" />

    </template>
  </div>
</template>

<style lang="scss" scoped>
.variant-cell {
  width: 30px;

  i {
    cursor: pointer;
  }
}
</style>
